import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { useAppContext } from '../AppContext';
import { LockIcon, SparklesIcon, CalendarIcon, BriefcaseIcon, MapIcon, MusicIcon, ShareIcon, RotateCcwIcon, SaveIcon, SlidersHorizontalIcon, CheckCircleIcon, XIcon, PaletteIcon, ShoppingBagIcon } from '../components/icons';
import { useDynamicStyle } from '../hooks/useDynamicStyle';
import { generateCapsuleWardrobe } from '../services/geminiService';
import { Logger } from '../services/logger';
import { checkGenerationQuota, incrementGenerationCount, getUserProfile, hasFeatureAccess, consumeOnDemandFeature } from '@shared/services/userService';
import { MEMBERSHIP_LIMITS, ON_DEMAND_FEATURES } from '../constants';
import LookbookCarousel from '../components/LookbookCarousel';

interface CapsuleItem {
    category: string;
    name: string;
    color: string;
    reason: string;
}

interface CapsuleResult {
    name: string;
    description: string;
    colorPalette: string[];
    items: CapsuleItem[];
    outfitCombinations: string[];
}

const CapsulePaletteItem: React.FC<{ color: string }> = ({ color }) => {
    const styleRef = useDynamicStyle<HTMLDivElement>({ '--palette-color': color });
    return (
        <div className="flex flex-col items-center gap-2">
            <div
                ref={styleRef}
                className="w-14 h-14 rounded-2xl shadow-inner border border-black/5 bg-[var(--palette-color)]"
            />
            <span className="text-[10px] font-mono text-gray-400">{color}</span>
        </div>
    );
};

const CapsuleItem: React.FC<{ item: any }> = ({ item }) => {
    const styleRef = useDynamicStyle<HTMLDivElement>({ '--item-color': item.color });
    return (
        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-600 flex gap-4 items-start">
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 truncate px-1">{item.category}</span>
            </div>
            <div>
                <h4 className="font-bold text-gray-900 dark:text-white text-sm">{item.name}</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.reason}</p>
                <div className="flex items-center gap-1.5 mt-2">
                    <div
                        ref={styleRef}
                        className="w-3 h-3 rounded-full border border-black/10 bg-[var(--item-color)]"
                    />
                    <span className="text-[10px] text-gray-500">{item.color}</span>
                </div>
            </div>
        </div>
    );
};

const CapsuleGeneratorPage: React.FC = () => {
    const { t, lang } = useTranslation();
    const { userProfile, currentUserId, updateUserProfile } = useAppContext();
    const [season, setSeason] = useState('Spring');
    const [occasion, setOccasion] = useState('Weekend Casual');
    const [isLoading, setIsLoading] = useState(false);
    const [capsule, setCapsule] = useState<CapsuleResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Access control: Guest users are locked, registered users (basic/premium/advanced) use quota
    const isGuest = currentUserId?.includes('guest') ?? true;
    const isLocked = isGuest || !hasFeatureAccess(userProfile, 'capsule_generator', 'premium');

    useSEO(
        t('capsuleGenerator_seo_title'),
        t('capsuleGenerator_seo_desc'),
        t('capsuleGenerator_seo_keywords').split(',').map(k => k.trim())
    );

    const handleGenerate = async () => {
        if (!checkGenerationQuota(userProfile, currentUserId, 'capsule_generator')) {
            const level = userProfile?.membershipLevel || 'basic';
            const limit = (MEMBERSHIP_LIMITS as any)[level];
            setError(t('error_quota_exceeded', { limit }));
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const result = await generateCapsuleWardrobe(userProfile, season, occasion, lang as any);
            setCapsule(result);

            // If user has on-demand credit specifically for this, consume it
            if (userProfile.purchasedFeatures?.['capsule_generator']?.remainingAmount! > 0) {
                consumeOnDemandFeature(currentUserId, 'capsule_generator');
            } else {
                // Otherwise increment normal generation count
                incrementGenerationCount(currentUserId);
            }
            updateUserProfile(getUserProfile(currentUserId));

            Logger.info('Capsule wardrobe generated successfully');
        } catch (err) {
            Logger.error('Failed to generate capsule', err);
            setError(t('error_generic_short'));
        } finally {
            setIsLoading(false);
        }
    };

    const seasons = [
        { id: 'Spring', label: t('capsuleGenerator_season_spring') },
        { id: 'Summer', label: t('capsuleGenerator_season_summer') },
        { id: 'Autumn', label: t('capsuleGenerator_season_autumn') },
        { id: 'Winter', label: t('capsuleGenerator_season_winter') },
    ];

    const occasions = [
        { id: 'Professional Workwear', label: t('capsuleGenerator_occasion_work'), icon: <BriefcaseIcon className="w-5 h-5" /> },
        { id: 'Weekend Casual', label: t('capsuleGenerator_occasion_casual'), icon: <CalendarIcon className="w-5 h-5" /> },
        { id: 'Travel & Vacation', label: t('capsuleGenerator_occasion_travel'), icon: <MapIcon className="w-5 h-5" /> },
        { id: 'Formal Events', label: t('capsuleGenerator_occasion_formal'), icon: <MusicIcon className="w-5 h-5" /> },
    ];

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 pt-20 flex flex-col">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl mx-auto px-4 w-full flex-grow mb-12"
            >
                <div className="text-center py-8">
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white mb-4">
                        {t('capsuleGenerator_title')}
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-10">
                        {t('capsuleGenerator_subtitle')}
                    </p>

                    {isLocked ? (
                        <div className="p-12 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl border-2 border-indigo-200 dark:border-purple-700 text-center shadow-xl">
                            <LockIcon className="w-16 h-16 text-indigo-500 dark:text-purple-400 mx-auto mb-6" />
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                                {isGuest ? t('feature_locked_guest') : t('on_demand_feature_capsule_generator_title')}
                            </h3>
                            <p className="text-gray-700 dark:text-gray-300 mb-8 max-w-md mx-auto">
                                {isGuest ? t('capsuleGenerator_premium_required') : t('on_demand_feature_capsule_generator_desc')}
                            </p>
                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={() => window.location.href = '/profile'}
                                    className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all transform hover:scale-105"
                                >
                                    {isGuest ? t('upgrade_membership') : t('upgrade_membership')}
                                </button>
                                {!isGuest && (
                                    <button
                                        onClick={() => {
                                            const feature = ON_DEMAND_FEATURES.find(f => f.id === 'capsule_generator');
                                            if (feature) {
                                                // Ideally we'd have a way to trigger the booking from here
                                                // Since we don't have the PaymentModal logic here, we redirect to profile
                                                window.location.href = '/profile';
                                            }
                                        }}
                                        className="px-8 py-3 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-bold rounded-xl border border-gray-200 dark:border-gray-600 transition-all shadow-sm"
                                    >
                                        {t('common_book')}
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Left Column: Controls */}
                            <div className="lg:col-span-1 space-y-6">
                                <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 text-left">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">
                                        {t('capsuleGenerator_select_season')}
                                    </label>
                                    <div className="grid grid-cols-2 gap-2 mb-6">
                                        {seasons.map(s => (
                                            <button
                                                key={s.id}
                                                onClick={() => setSeason(s.id)}
                                                className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${season === s.id
                                                    ? 'bg-indigo-600 text-white shadow-md'
                                                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                                            >
                                                {s.label}
                                            </button>
                                        ))}
                                    </div>

                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">
                                        {t('capsuleGenerator_select_occasion')}
                                    </label>
                                    <div className="space-y-2 mb-8">
                                        {occasions.map(o => (
                                            <button
                                                key={o.id}
                                                onClick={() => setOccasion(o.id)}
                                                className={`w-full flex items-center gap-3 py-3 px-4 rounded-xl text-left transition-all ${occasion === o.id
                                                    ? 'bg-indigo-600 text-white shadow-md'
                                                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                                            >
                                                {o.icon}
                                                <span className="font-medium">{o.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        onClick={handleGenerate}
                                        disabled={isLoading}
                                        className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:scale-100 shadow-lg"
                                    >
                                        {isLoading ? (
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                {t('capsuleGenerator_generating')}
                                            </div>
                                        ) : (
                                            <>
                                                <SparklesIcon className="w-5 h-5" />
                                                {t('capsuleGenerator_generate_btn')}
                                            </>
                                        )}
                                    </button>

                                    {error && (
                                        <p className="mt-4 text-red-500 text-sm text-center font-medium">{error}</p>
                                    )}
                                </div>
                            </div>

                            {/* Right Column: Results */}
                            <div className="lg:col-span-2">
                                <AnimatePresence mode="wait">
                                    {capsule ? (
                                        <motion.div
                                            key="result"
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.95 }}
                                            className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-2xl text-left"
                                        >
                                            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-8 text-white">
                                                <h2 className="text-3xl font-serif font-bold mb-2">{capsule.name}</h2>
                                                <p className="text-indigo-50 opacity-90">{capsule.description}</p>
                                            </div>

                                            <div className="p-8 space-y-10">
                                                {/* Palette */}
                                                <section>
                                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                                        <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                                                        {t('capsuleGenerator_result_palette')}
                                                    </h3>
                                                    <div className="flex gap-3 flex-wrap">
                                                        {capsule.colorPalette.map((color, idx) => (
                                                            <CapsulePaletteItem key={idx} color={color} />
                                                        ))}
                                                    </div>
                                                </section>

                                                {/* Items */}
                                                <section>
                                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                                        <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                                                        {t('capsuleGenerator_result_items')}
                                                    </h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {capsule.items.map((item, idx) => (
                                                            <CapsuleItem key={idx} item={item} />
                                                        ))}
                                                    </div>
                                                </section>

                                                {/* Combinations */}
                                                <section>
                                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                                        <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                                                        {t('capsuleGenerator_result_combinations')}
                                                    </h3>
                                                    <ul className="space-y-3">
                                                        {capsule.outfitCombinations.map((combo, idx) => (
                                                            <li key={idx} className="flex gap-3 text-sm text-gray-600 dark:text-gray-300 bg-indigo-50/30 dark:bg-indigo-900/10 p-4 rounded-xl border border-indigo-100/50 dark:border-indigo-900/20">
                                                                <span className="font-serif italic text-indigo-400 dark:text-indigo-600">0{idx + 1}</span>
                                                                {combo}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </section>
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-gray-50 dark:bg-gray-800 rounded-3xl border-2 border-dashed border-gray-200 dark:border-gray-700 min-h-[400px]">
                                            <SparklesIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                                            <p className="text-gray-400 dark:text-gray-500 italic max-w-xs">
                                                {t('capsuleGenerator_empty_state')}
                                            </p>
                                        </div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Lookbook Carousel Section */}
            <div className="border-t border-gray-100 dark:border-gray-800">
                <LookbookCarousel />
            </div>

            <Footer onNavigate={() => { }} />
        </div>
    );
};

export default CapsuleGeneratorPage;
